import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export interface SystemSetting {
  key: string;
  value: unknown;
  updated_by: string | null;
  created_at: string;
}

export function useSystemSettings() {
  const { user, isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();

  const { data: allRows = [], isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value, updated_by, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as SystemSetting[];
    },
    enabled: isSuperAdmin,
  });

  const settings = useMemo(() => {
    const seen = new Set<string>();
    const latest: SystemSetting[] = [];
    for (const row of allRows) {
      if (!seen.has(row.key)) {
        seen.add(row.key);
        latest.push(row);
      }
    }
    return latest;
  }, [allRows]);

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const { error } = await supabase
        .from('system_settings')
        .insert([{ key, value: value as never, updated_by: user?.id }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
    },
  });

  function getSettingHistory(key: string): SystemSetting[] {
    return allRows.filter((s) => s.key === key);
  }

  return { settings, isLoading, updateSetting, getSettingHistory };
}
